import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiErrorHidingServerText } from "@/utils";
import queryClient from "@/query-client";
import {
  ProductProhibitedKeyword,
  ProductProhibitedKeywordWithRemoval,
  BulkAddProhibitedKeywordsResponse,
} from "@/types/prohibited-keyword";
import { DEFAULT_STALE_TIME } from "@/constants";
import { getGlobalKeywordSet } from "@/resources/global-prohibited-keyword/global-prohibited-keyword.api";

// AUT-41: Product-level prohibited keywords (narrowest tier). Scoped by product_id.
const getQueryKey = (productId: number) =>
  ["product-prohibited-keywords", "list", productId] as const;

type Snapshot = { queryKey: readonly unknown[]; data: ProductProhibitedKeyword[] | undefined };

const takeSnapshot = (productId: number): Snapshot => {
  const queryKey = getQueryKey(productId);
  return { queryKey, data: queryClient.getQueryData<ProductProhibitedKeyword[]>(queryKey) };
};

const restoreSnapshot = (snapshot: Snapshot) => {
  queryClient.setQueryData(snapshot.queryKey, snapshot.data);
};

let optimisticIdCounter = -1;

export const useGetProductProhibitedKeywords = (productId: number | null) =>
  useQuery<ProductProhibitedKeyword[]>({
    queryKey: ["product-prohibited-keywords", "list", productId],
    queryFn: () =>
      apiService.get<ProductProhibitedKeyword[]>("/product-prohibited-keywords", {
        product_id: productId,
      }),
    enabled: !!productId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useAddProductProhibitedKeyword = () =>
  useMutation<
    ProductProhibitedKeywordWithRemoval,
    unknown,
    { product_id: number; keyword: string; origin?: string },
    { snapshot: Snapshot }
  >({
    mutationFn: (data) =>
      apiService.post<ProductProhibitedKeywordWithRemoval>("/product-prohibited-keywords", data),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.product_id);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.product_id);

      const keyword = variables.keyword.trim().toLowerCase();

      // AUT-240: the Global list already blocks this keyword, so the server drops it.
      // Skip the optimistic row so it doesn't flash into the list and then vanish.
      if (getGlobalKeywordSet().has(keyword)) {
        return { snapshot };
      }

      const optimistic: ProductProhibitedKeyword = {
        id: optimisticIdCounter--,
        product_id: variables.product_id,
        organization_id: 0,
        keyword,
        origin: (variables.origin as ProductProhibitedKeyword["origin"]) ?? "manual",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ProductProhibitedKeyword[]>(queryKey, (prev) =>
        prev ? [...prev, optimistic] : [optimistic],
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(error);
    },
    onSuccess: (serverData, variables) => {
      const queryKey = getQueryKey(variables.product_id);
      queryClient.setQueryData<ProductProhibitedKeyword[]>(queryKey, (prev) => {
        if (!prev) return prev;
        return prev.map((k) =>
          k.id < 0 && k.keyword === variables.keyword.trim().toLowerCase() ? serverData : k,
        );
      });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });

export const useBulkAddProductProhibitedKeywords = () =>
  useMutation<
    BulkAddProhibitedKeywordsResponse,
    unknown,
    { product_id: number; keywords: string[]; origin?: string }
  >({
    mutationFn: (data) =>
      apiService.post<BulkAddProhibitedKeywordsResponse>(
        "/product-prohibited-keywords/bulk",
        data,
      ),
    onSuccess: (_serverData, variables) => {
      queryClient.invalidateQueries({ queryKey: getQueryKey(variables.product_id) });
    },
    onSettled: () => {
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
    onError: (error) => handleApiErrorHidingServerText(error),
  });

export const useDeleteProductProhibitedKeyword = () =>
  useMutation<unknown, unknown, { id: number; productId: number }, { snapshot: Snapshot }>({
    mutationFn: ({ id }) => apiService.delete(`/product-prohibited-keywords/${id}`),
    onMutate: async (variables) => {
      const queryKey = getQueryKey(variables.productId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = takeSnapshot(variables.productId);

      queryClient.setQueryData<ProductProhibitedKeyword[]>(queryKey, (prev) =>
        prev ? prev.filter((k) => k.id !== variables.id) : prev,
      );

      return { snapshot };
    },
    onError: (error, _variables, context) => {
      if (context) restoreSnapshot(context.snapshot);
      handleApiErrorHidingServerText(error);
    },
    onSettled: (_data, _error, variables) => {
      // AUT-220: reconcile the list with the server after the delete settles. Without
      // this the optimistic write was the only source of truth (staleTime is 5 min),
      // so a rolled-back failure could resurrect a row a later delete had removed.
      queryClient.invalidateQueries({ queryKey: getQueryKey(variables.productId) });
      queryClient.refetchQueries({ queryKey: ["search-term-sets"] });
    },
  });
